import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../lib';

const alertVariants = cva(
  'relative w-full rounded-lg border p-4 [&>svg~*]:pl-7 [&>svg]:absolute [&>svg]:left-4 [&>svg]:text-foreground [&>div]:ml-1',
  {
    variants: {
      variant: {
        default: 'bg-background text-foreground',
        destructive:
          'border-status-red bg-status-red text-foreground [&>svg]:text-destructive',
        alert:
          'border-status-yellow bg-status-yellow text-foreground [&>svg]:text-yellow-600',
        success:
          'border-status-green bg-status-green text-foreground [&>svg]:text-green-600',
        info: 'border-card-secondary bg-card-secondary text-foreground [&>svg]:text-foreground',
      },
      iconPosition: {
        center: '[&>svg]:top-1/2 [&>svg]:-translate-y-1/2',
        top: '[&>svg]:top-4',
      },
    },
    defaultVariants: {
      variant: 'default',
      iconPosition: 'center',
    },
  }
);

const Alert = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants>
>(({ className, variant, iconPosition, ...props }, ref) => (
  <div
    ref={ref}
    role="alert"
    className={cn(alertVariants({ variant, iconPosition }), className)}
    {...props}
  />
));
Alert.displayName = 'Alert';

const AlertTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, children, ...props }, ref) => (
  <h5
    ref={ref}
    className={cn('mb-1 font-medium leading-none tracking-tight', className)}
    {...props}
  >
    {children}
  </h5>
));
AlertTitle.displayName = 'AlertTitle';

const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('text-sm [&_p]:leading-relaxed', className)}
    {...props}
  />
));
AlertDescription.displayName = 'AlertDescription';

export { Alert, AlertTitle, AlertDescription };
